import { Clock, TrendingUp, Search, X } from 'lucide-react'
import { cn } from '../../lib/utils'

interface SearchSuggestionsProps {
  isOpen: boolean
  searchTerm: string
  recentSearches: string[]
  suggestions: string[]
  onSelectSearch: (search: string) => void
  onRemoveRecent: (search: string) => void
  onClearRecent: () => void
  className?: string
}

export function SearchSuggestions({
  isOpen,
  searchTerm,
  recentSearches,
  suggestions,
  onSelectSearch,
  onRemoveRecent,
  onClearRecent,
  className
}: SearchSuggestionsProps) {
  if (!isOpen) return null

  const hasRecent = recentSearches.length > 0
  const hasSuggestions = suggestions.length > 0
  const showContent = hasRecent || hasSuggestions

  if (!showContent) return null

  return (
    <div
      className={cn(
        "absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50 max-h-[400px] overflow-y-auto",
        className
      )}
    >
      {/* Recent Searches */}
      {hasRecent && !searchTerm && (
        <div className="border-b border-gray-100">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Recent Searches</span>
            </div>
            <button
              onClick={onClearRecent}
              className="text-xs text-gray-500 hover:text-gray-700 font-medium"
            >
              Clear
            </button>
          </div>
          <div className="py-2">
            {recentSearches.map((search, index) => (
              <button
                key={`recent-${index}`}
                onClick={() => onSelectSearch(search)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors group"
              >
                <div className="flex items-center space-x-3 flex-1">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-700">{search}</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemoveRecent(search)
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-all"
                  aria-label={`Remove ${search}`}
                >
                  <X className="h-3 w-3 text-gray-500" />
                </button>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search Suggestions */}
      {hasSuggestions && searchTerm && (
        <div>
          <div className="flex items-center space-x-2 px-4 py-3 bg-gray-50">
            <TrendingUp className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Suggestions</span>
          </div>
          <div className="py-2">
            {suggestions.map((suggestion, index) => (
              <button
                key={`suggestion-${index}`}
                onClick={() => onSelectSearch(suggestion)}
                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                <Search className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-700">{suggestion}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
